import React, { FC } from 'react'
import { DiscType } from '@crystal-eyes/types'
import classNamesBind from 'classnames/bind'
import { toArchetype, toDiscType } from '@dashboard/lib/personality'
import AvatarCircle from '@crystal-eyes/components/elements/Avatar/AvatarCircle'
import Image from 'next/image'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

interface OwnProps {
  photoURL?: string | null
  fullName?: string
  size?: number
  discType?: DiscType | undefined
  showName?: boolean
  showNameOnHover?: boolean
  discDegrees?: number
  emptyDiscColor?: string
  profilePlaceholder?: boolean
  archetypeText?: string
}

const ComparisonProfileImage: FC<OwnProps> = ({
  photoURL,
  fullName,
  size,
  showName,
  showNameOnHover,
  discDegrees,
  emptyDiscColor,
  profilePlaceholder,
  archetypeText,
  discType,
}: OwnProps) => {
  // Partials
  const renderImage = () => {
    if (profilePlaceholder) {
      return (
        <Image
          width={size}
          height={size}
          src="/static/images/profile-placeholder.svg"
          alt={fullName || 'Profile Placeholder'}
        />
      )
    }
    if (photoURL) {
      return (
        <AvatarCircle
          photoUrl={photoURL}
          r={size ? size / 2 : 36}
          discType={discType || undefined}
          name={fullName || ''}
        />
      )
    }

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="325.587"
        height="325.587"
        viewBox="0 0 325.587 325.587"
        className={css.empty}
      >
        <defs>
          <linearGradient
            id="a"
            y1="0.5"
            x2="1"
            y2="0.5"
            gradientUnits="objectBoundingBox"
          >
            <stop offset="0" stopColor="#b8e3f8" />
            <stop offset="1" stopColor="#e8f6fc" stopOpacity="0.635" />
          </linearGradient>
          <clipPath id="b">
            <circle
              className={css.svg_a}
              cx="115.247"
              cy="115.247"
              r="115.247"
            />
          </clipPath>
        </defs>
        <g transform="translate(-559.487 -163.672)">
          <circle
            className={classNames('svg_b', emptyDiscColor)}
            cx="115.112"
            cy="115.112"
            r="115.112"
            transform="translate(722.281 163.672) rotate(45)"
          />
          <g transform="translate(665.052 71.977)">
            <g className={css.svg_c} transform="translate(-58.052 139.023)">
              <g transform="translate(36.668 34.663)">
                <circle
                  className={css.svg_d}
                  cx="41.929"
                  cy="41.929"
                  r="41.929"
                  transform="translate(36.17)"
                />
                <path
                  className={css.svg_d}
                  d="M26.536,346.941l-45.343-26.03a17.781,17.781,0,0,1-17.818-17.742,17.8,17.8,0,0,1,.683-4.918,80.672,80.672,0,0,1,154.83,0,17.778,17.778,0,0,1-12.211,21.977C105.075,320.684,28.2,346.945,26.536,346.941Z"
                  transform="translate(36.626 -138.248)"
                />
              </g>
            </g>
          </g>
        </g>
      </svg>
    )
  }

  const renderNameTooltip = () => {
    if (!showNameOnHover) return null

    const style = {
      bottom: `${size}px`,
      left: `${size}px`,
    }
    return (
      <div className={css.tooltip} style={style}>
        {fullName}
      </div>
    )
  }

  const renderArchetype = () => {
    if (archetypeText !== undefined) {
      return (
        <div className={css.archetypeContainer}>
          <div className={css.archetype}>{archetypeText}</div>
        </div>
      )
    }

    if (discDegrees === undefined) return null

    const discType = toDiscType(discDegrees)

    return (
      <div className={css.archetypeContainer}>
        <div className={classNames('archetype', discType)}>
          {toArchetype(discType)}
        </div>
      </div>
    )
  }

  const renderName = () => {
    if (!showName || !fullName) return null

    return (
      <div className={css.nameContainer}>
        <div className={css.name}>{fullName}</div>
      </div>
    )
  }

  const styles = {
    width: `${size || 128}px`,
    height: `${size || 128}px`,
  }

  return (
    <div className={css.container}>
      <div className={css.imageContainer} style={styles}>
        {renderImage()}
      </div>
      {renderNameTooltip()}
      {renderArchetype()}
      {renderName()}
    </div>
  )
}

export default ComparisonProfileImage
